import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  clearPendingVerificationEmail,
  getPendingVerificationEmail,
} from '@/modules/auth/lib/pendingVerificationEmail';
import { apiClient, isApiError } from '@/shared/lib/apiClient';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';

const searchSchema = z.object({
  token: z.string().default(''),
});

export const Route = createFileRoute('/auth/verify-email')({
  validateSearch: searchSchema,
  component: VerifyEmailPage,
});

type VerifyState = 'pending' | 'success' | 'error';
type VerifyErrorCode = 'TOKEN_EXPIRED' | 'TOKEN_INVALID' | 'TOKEN_ALREADY_USED' | 'generic';

function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const [state, setState] = useState<VerifyState>('pending');
  const [errorCode, setErrorCode] = useState<VerifyErrorCode>('generic');
  const [resending, setResending] = useState(false);
  const [resentSuccess, setResentSuccess] = useState(false);
  const verifyStarted = useRef(false);
  const pendingEmail = getPendingVerificationEmail();

  useEffect(() => {
    const normalized = token.trim();
    if (!normalized || normalized.length !== 64 || !/^[a-f0-9]+$/.test(normalized)) {
      setErrorCode('TOKEN_INVALID');
      setState('error');
      return;
    }

    if (verifyStarted.current) return;
    verifyStarted.current = true;

    apiClient
      .post('/api/v1/auth/verify-email', { token: normalized })
      .then(() => {
        clearPendingVerificationEmail();
        setState('success');
      })
      .catch((err: unknown) => {
        if (!isApiError(err)) {
          setState('error');
          return;
        }
        if (err.code === 'TOKEN_EXPIRED') {
          setErrorCode('TOKEN_EXPIRED');
        } else if (err.code === 'TOKEN_INVALID') {
          setErrorCode('TOKEN_INVALID');
        } else if (err.code === 'TOKEN_ALREADY_USED') {
          setErrorCode('TOKEN_ALREADY_USED');
        } else {
          setErrorCode('generic');
        }
        setState('error');
      });
  }, [token]);

  useEffect(() => {
    if (state !== 'success') return;
    const timer = window.setTimeout(() => {
      void navigate({ to: '/auth/login', replace: true });
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [state, navigate]);

  const handleResend = async () => {
    const email = getPendingVerificationEmail();
    if (!email) {
      setErrorCode('generic');
      setState('error');
      return;
    }

    setResending(true);
    setResentSuccess(false);
    try {
      await apiClient.post('/api/v1/auth/resend-verification', { email });
      setResentSuccess(true);
    } finally {
      setResending(false);
    }
  };

  const errorMessage =
    errorCode === 'TOKEN_EXPIRED'
      ? t('auth.verifyEmail.expiredToken')
      : errorCode === 'TOKEN_ALREADY_USED'
        ? t('auth.verifyEmail.alreadyUsed')
        : t('auth.verifyEmail.invalidToken');

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {state === 'pending'
            ? t('auth.verifyEmail.title')
            : state === 'success'
              ? t('auth.verifyEmail.successTitle')
              : t('auth.verifyEmail.errorTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {state === 'pending' && (
          <p className="text-sm text-muted-foreground animate-pulse">
            {t('auth.verifyEmail.subtitle')}
          </p>
        )}

        {state === 'success' && (
          <>
            <Alert variant="success">
              <AlertDescription>{t('auth.verifyEmail.successMessage')}</AlertDescription>
            </Alert>
            <Link
              to="/auth/login"
              className="block text-center text-sm text-primary hover:underline font-medium"
            >
              {t('auth.verifyEmail.signIn')}
            </Link>
          </>
        )}

        {state === 'error' && (
          <>
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
            {resentSuccess && (
              <Alert variant="success">
                <AlertDescription>{t('auth.verifyEmailSent.resentSuccess')}</AlertDescription>
              </Alert>
            )}
            <Button
              variant="outline"
              className="w-full"
              isLoading={resending}
              disabled={!pendingEmail}
              onClick={() => void handleResend()}
            >
              {t('auth.verifyEmail.resend')}
            </Button>
            {!pendingEmail && (
              <p className="text-xs text-muted-foreground text-center">
                {t('auth.verifyEmail.resendNeedsEmail')}
              </p>
            )}
            <Link
              to="/auth/login"
              className="block text-center text-sm text-primary hover:underline"
            >
              {t('auth.verifyEmail.signIn')}
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  );
}
